import { type BasePerformanceRequestFilterParams } from "features/Library/Common/models";
import { lineChartFactory } from "features/Library/Common/utils/performanceUtils";
import { reportingColors } from "features/Library/Common/utils/constants";
import { ThreatDefenseFlowStatusEnum, ThreatDefenseEmailStatusEnum } from "../Details/types";
import type { DataPoint } from "components/charts/types/HorizontalBarChart";
import { type CardReportingItem } from "components/cards/CardReporting/CardReporting";
import { type QueryFunctionContext } from "@tanstack/react-query";
import { type ChartPeriod } from "hooks/useChartPeriodMeasure";
import { threatDefenseReportingService } from "../services/threatDefenseReportingService";

type QueryContextCampaign = QueryFunctionContext<
  [_key: string, filter: BasePerformanceRequestFilterParams, campaignId: number]
>;

type QueryContextCampaignRun = QueryFunctionContext<
  [_key: string, filter: BasePerformanceRequestFilterParams, campaignId?: number, campaignRunId?: number]
>;

type QueryContextPeriod = QueryFunctionContext<
  [
    _key: string,
    filter: BasePerformanceRequestFilterParams,
    period: ChartPeriod,
    campaignId?: number,
    campaignRunId?: number,
  ]
>;

// Phishing Campaign Queries

export const getThreatDefensePerformanceLineChart = async ({ queryKey, signal }: QueryContextPeriod) => {
  const [_key, filter, period, campaignId, campaignRunId] = queryKey;
  const { data } = await threatDefenseReportingService.getPhishingCampaignLineChart(
    { aggregation: period, campaignId, campaignRunId, ...filter },
    signal!,
  );

  const formattedData = lineChartFactory(data);

  return {
    lineData: {
      xData: [formattedData.Date, formattedData.Date, formattedData.Date, formattedData.Date],
      yData: [
        formattedData.EmailsSent,
        formattedData.EmailsOpened,
        formattedData.EmailsClicked,
        formattedData.DataSubmitted,
      ],
    },
  };
};

export const getThreatDefensePerformanceEngagement = async ({ queryKey, signal }: QueryContextCampaignRun) => {
  const [_key, filter, campaignId, campaignRunId] = queryKey;
  const { data } = await threatDefenseReportingService.getPhishingCampaignEngagement(
    { campaignId, campaignRunId, ...filter },
    signal!,
  );

  const barData: DataPoint[] = [
    {
      id: ThreatDefenseEmailStatusEnum.SENT,
      category: ThreatDefenseEmailStatusEnum.SENT,
      value: data.EmailsSent,
      fillColor: reportingColors.blue,
    },
    {
      id: ThreatDefenseEmailStatusEnum.OPENED,
      category: ThreatDefenseEmailStatusEnum.OPENED,
      value: data.EmailsOpened,
      fillColor: reportingColors.greenLight,
    },
    {
      id: ThreatDefenseEmailStatusEnum.CLICKED,
      category: ThreatDefenseEmailStatusEnum.CLICKED,
      value: data.EmailsClicked,
      fillColor: reportingColors.yellow,
    },
    {
      id: ThreatDefenseEmailStatusEnum.SUBMITTED,
      category: ThreatDefenseEmailStatusEnum.SUBMITTED,
      value: data.DataSubmitted,
      fillColor: reportingColors.red,
    },
  ];

  const cards: CardReportingItem[] = [
    {
      statDescription: "Emails Sent",
      stat: data.EmailsSent,
      popupBody: "The number of emails sent.",
    },
    {
      statDescription: "Opened",
      stat: data.EmailsOpened,
      popupBody: "The number of emails opened.",
    },
    {
      statDescription: "Clicked",
      stat: data.EmailsClicked,
      popupBody: "The number of clicks on email links or buttons.",
    },
    {
      statDescription: "Submitted Data",
      stat: data.DataSubmitted,
      popupBody: "The number of submissions of personal data.",
    },
  ];

  return { barData, cards };
};

export const getThreatDefensePerformanceTable = async ({ queryKey, signal }: QueryContextCampaign) => {
  const [_key, filter, campaignId] = queryKey;
  return (await threatDefenseReportingService.getPhishingCampaignTable({ campaignId, ...filter }, signal!)).data;
};

export const getThreatDefensePerformancePeopleTable = async ({ queryKey, signal }: QueryContextCampaignRun) => {
  const [_key, filter, campaignId, campaignRunId] = queryKey;
  return (
    await threatDefenseReportingService.getPhishingCampaignPeopleTable(
      { campaignId, campaignRunId, ...filter },
      signal!,
    )
  ).data;
};

// Remedial Training Queries

export const getRemedialTrainingLineChart = async ({ queryKey, signal }: QueryContextPeriod) => {
  const [_key, filter, period, campaignId] = queryKey;
  const { data } = await threatDefenseReportingService.getRemedialTrainingLineChart(
    { aggregation: period, campaignId, ...filter },
    signal!,
  );

  const formattedData = lineChartFactory(data);

  return {
    lineData: {
      xData: [formattedData.Date, formattedData.Date, formattedData.Date],
      yData: [formattedData.TotalNotStarted, formattedData.TotalStarts, formattedData.TotalCompletes],
    },
  };
};

export const getRemedialTrainingEngagement = async ({ queryKey, signal }: QueryContextCampaign) => {
  const [_key, filter, campaignId] = queryKey;
  const { data } = await threatDefenseReportingService.getRemedialTrainingEngagement(
    { campaignId, ...filter },
    signal!,
  );

  const barData: DataPoint[] = [
    {
      id: ThreatDefenseFlowStatusEnum.NOT_STARTED,
      category: ThreatDefenseFlowStatusEnum.NOT_STARTED,
      value: data.TotalNotStarted,
      fillColor: reportingColors.greyLight,
    },
    {
      id: ThreatDefenseFlowStatusEnum.IN_PROGRESS,
      category: ThreatDefenseFlowStatusEnum.IN_PROGRESS,
      value: data.TotalStarts,
      fillColor: reportingColors.yellow,
    },
    {
      id: ThreatDefenseFlowStatusEnum.COMPLETED,
      category: ThreatDefenseFlowStatusEnum.COMPLETED,
      value: data.TotalCompletes,
      fillColor: reportingColors.greenLight,
    },
  ];

  const cards: CardReportingItem[] = [
    {
      statDescription: ThreatDefenseFlowStatusEnum.NOT_STARTED,
      stat: data.TotalNotStarted,
      popupBody: "The number of remedial training flows not started.",
    },
    {
      statDescription: ThreatDefenseFlowStatusEnum.IN_PROGRESS,
      stat: data.TotalStarts,
      popupBody: "The number of remedial training flows begun.",
    },
    {
      statDescription: ThreatDefenseFlowStatusEnum.COMPLETED,
      stat: data.TotalCompletes,
      popupBody: "The number of remedial training flows completed.",
    },
  ];

  return {
    barData,
    cards,
    TotalStarts: data.TotalStarts,
    TotalCompletes: data.TotalCompletes,
    TotalNotStarted: data.TotalNotStarted,
  };
};

export const getRemedialTrainingUsers = async ({ queryKey, signal }: QueryContextCampaign) => {
  const [_key, filter, campaignId] = queryKey;
  return (await threatDefenseReportingService.getRemedialTrainingPeopleTable({ campaignId, ...filter }, signal!)).data;
};
